import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { ApprovalGate } from "@/components/ApprovalGate";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { DocumentTitleManager } from "./components/DocumentTitleManager";
import ErrorBoundary from "./components/ErrorBoundary";
import { CSSVariablesProvider } from "./components/providers/CSSVariablesProvider";
import ProjectCraftspace from "./layouts/ProjectCraftspace";
import Auth from "./pages/Auth";
import { CMSPanel } from "./pages/CMS";
import Dashboard from "./pages/Dashboard";
import { EditorPanel } from "./pages/Editor";
import Maps from "./pages/Maps";
import NotFound from "./pages/NotFound";
import PasswordResetTest from "./pages/PasswordResetTest";
import { SettingsPanel } from "./pages/ProjectSettings";
import { SkinPanel } from "./pages/Skin";
import Assets from "./pages/Assets";
import TrashPage from "./pages/Trash";
import Variables from "./pages/Variables";
import CraftspaceSettings from "./pages/CraftspaceSettings";
import UserPreferences from "./pages/UserPreferences";

// Import diagnostics to make console testing available
import "@/utils/s3Diagnostics";
import "@/utils/quickS3Test";

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            {/* Electron: fixed drag region across the top so the window can be moved.
                Height 28px matches trafficLightPosition y=16 + button radius. */}
            {isElectron() && (
              <div className="electron-drag fixed top-0 left-0 right-0 h-7 z-[500] pointer-events-none" />
            )}
            <Toaster />
            <Sonner />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <DocumentTitleManager />
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/login" element={<Auth />} />
                <Route path="/login/reset-password" element={<Auth />} />
                <Route path="/debug/password-reset" element={<PasswordResetTest />} />
                <Route
                  path="/dashboard"
                  element={
                    <ApprovalGate>
                      <Dashboard />
                    </ApprovalGate>
                  }
                />
                <Route
                  path="/user-preferences"
                  element={
                    <ApprovalGate>
                      <UserPreferences />
                    </ApprovalGate>
                  }
                />
                <Route
                  path="craftspace/:craftspaceId/settings"
                  element={
                    <ApprovalGate>
                      <CraftspaceSettings />
                    </ApprovalGate>
                  }
                />
                <Route
                  path=":projectId"
                  element={
                    <ApprovalGate>
                      <CSSVariablesProvider>
                        <ProjectCraftspace />
                      </CSSVariablesProvider>
                    </ApprovalGate>
                  }
                >
                  <Route index element={<EditorPanel />} />
                  <Route path="editor" element={<EditorPanel />} />
                  <Route path="maps" element={<Maps />} />
                  <Route path="skin" element={<SkinPanel />} />
                  <Route path="cms" element={<CMSPanel />} />
                  <Route path="assets" element={<Assets />} />
                  <Route path="variables" element={<Variables />} />
                  <Route path="settings" element={<SettingsPanel />} />
                  <Route path="trash" element={<TrashPage />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
